import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { api, fmtDate } from '../components/payment-plans/utils';
import { PRESETS, PeriodPicker, presetRange } from '../components/payment-plans/PeriodPicker';
import { KpiCards } from '../components/payment-plans/KpiCards';
import { CollectionChart } from '../components/payment-plans/CollectionChart';
import { FollowUpList } from '../components/payment-plans/FollowUpList';
import { DepositAlertList } from '../components/payment-plans/DepositAlertList';
import { SuggestionsPanel } from '../components/payment-plans/SuggestionsPanel';
import { PlanTable } from '../components/payment-plans/PlanTable';
import { PlanDialog } from '../components/payment-plans/PlanDialog';
import { PlanDrawer } from '../components/payment-plans/PlanDrawer';
import { FollowUpDialog } from '../components/payment-plans/FollowUpDialog';
import { useRefreshJob } from '../components/payment-plans/useRefreshJob';

const Header = ({ data, refresh }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8B9D83]">Admin · Finance</p>
      <h1 className="mt-1 font-heading text-4xl font-semibold text-[#3A3530] sm:text-5xl" data-testid="payment-plans-title">Payment Plans</h1>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-[#6B6560]">
        <Lock className="h-3.5 w-3.5" />Halaxy is the source of truth for payments and balances (read-only). This register tracks agreements and follow-ups.
      </p>
    </div>
    <div className="flex flex-col items-start gap-1 lg:items-end">
      <Button onClick={refresh.start} disabled={refresh.running} className="bg-[#3A3530] text-white hover:bg-[#2A2520]" data-testid="refresh-halaxy-btn">
        <RefreshCw className={`mr-2 h-4 w-4 ${refresh.running ? 'animate-spin' : ''}`} />{refresh.running ? 'Refreshing…' : 'Refresh from Halaxy'}
      </Button>
      <span className="text-xs text-[#9B9590]" data-testid="last-synced-label">
        {refresh.running ? refresh.stage : `Last refreshed ${data?.last_synced_at ? fmtDate(data.last_synced_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never'} · auto-refreshes daily at 6am`}
      </span>
    </div>
  </div>
);

export default function PaymentPlansPage() {
  const [preset, setPreset] = useState('this_month');
  const [range, setRange] = useState(presetRange('this_month'));
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, mode: 'manual', source: null });
  const [drawerId, setDrawerId] = useState(null);
  const [followUp, setFollowUp] = useState({ plan: null, status: 'Follow Up Sent' });

  const load = useCallback(async () => {
    try { setData(await api(`?from=${range.from}&to=${range.to}`)); } catch (e) { toast.error(e.message); }
  }, [range]);
  const refresh = useRefreshJob(load);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) { window.location.href = '/admin-login'; return; }
    if (range.from && range.to && range.from <= range.to) load();
  }, [load, range]);

  const onPreset = (k) => { setPreset(k); if (k !== 'custom') setRange(presetRange(k)); };
  const onSaved = (plan, mode) => {
    setDialog((d) => ({ ...d, open: false }));
    setFollowUp((f) => ({ ...f, plan: null }));
    load();
    if (mode === 'manual' && plan && !plan.payments_loaded) refresh.start();
  };
  const dismiss = async (s) => {
    try { await api(`/suggestions/${s.invoice_id}/dismiss`, { method: 'POST' }); toast.success('Suggestion dismissed'); load(); } catch (e) { toast.error(e.message); }
  };

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#8B9D83] border-r-transparent" /></div>;
  }
  const drawerPlan = data.plans.find((p) => p.id === drawerId) || null;
  const followUps = data.plans.filter((p) => p.needs_follow_up).sort((a, b) => (a.overdue_reason === 'failed' ? -1 : 1) - (b.overdue_reason === 'failed' ? -1 : 1) || b.days_overdue - a.days_overdue);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="payment-plans-page">
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Payment Plans - Navigating Minds</title></Helmet>
      <div className="mx-auto max-w-7xl space-y-8 px-4 sm:px-6 lg:px-8">
        <Header data={data} refresh={refresh} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PeriodPicker preset={preset} range={range} onPreset={onPreset} onRange={setRange} />
          <span className="text-xs text-[#9B9590]" data-testid="period-label">{fmtDate(data.period.from)} to {fmtDate(data.period.to)}</span>
        </div>
        <KpiCards kpis={data.kpis} periodLabel={PRESETS[preset].toLowerCase()} />
        <FollowUpList plans={followUps} onFollowUp={(p) => setFollowUp({ plan: p, status: 'Follow Up Sent' })} onOpen={(p) => setDrawerId(p.id)} />
        <DepositAlertList alerts={data.deposit_alerts || []} />
        <CollectionChart data={data.kpis.chart} granularity={data.kpis.chart_granularity} />
        <PlanTable plans={data.plans} onOpen={(p) => setDrawerId(p.id)} />
        <SuggestionsPanel suggestions={data.suggestions} awaiting={data.awaiting_deposit}
          onConfirm={(s) => setDialog({ open: true, mode: 'suggestion', source: s })} onDismiss={dismiss}
          onManual={() => setDialog({ open: true, mode: 'manual', source: null })} />
      </div>
      <PlanDialog {...dialog} onClose={() => setDialog((d) => ({ ...d, open: false }))} onSaved={onSaved} />
      <PlanDrawer plan={drawerPlan} onClose={() => setDrawerId(null)} onSaved={() => load()}
        onEdit={(p) => setDialog({ open: true, mode: 'edit', source: p })}
        onFollowUp={(p, status) => setFollowUp({ plan: p, status })}
        onDeleted={() => { setDrawerId(null); load(); }} />
      <FollowUpDialog plan={followUp.plan} status={followUp.status} onClose={() => setFollowUp((f) => ({ ...f, plan: null }))} onSaved={onSaved} />
    </div>
  );
}
